import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import { resolveAsset } from "../utils/paths";
import {
  FaRegHeart,
  FaHandHoldingHeart,
  FaFileMedical,
  FaShareAlt,
  FaWalking,
  FaUsers,
} from "react-icons/fa";
import "./HealthDisability.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const SERVICES = [
  {
    icon: FaFileMedical,
    title: "Assessment & Support Camps",
    text: "We organize camps with specialist doctors for case assessment and distribute essential Aids & Appliances to identified beneficiaries, helping to minimize disability and enhance physical abilities.",
    image: "/assets/images/backgrounds/helth1.png",
    alt: "Health Camp",
    reverse: false,
  },
  {
    icon: FaShareAlt,
    title: "Referral Services",
    text: "We provide crucial referral services to connect Children and Persons with Disabilities (CWDs/PWDs) to appropriate service delivery points based on their individual needs.",
    image: "/assets/images/backgrounds/helth2.png",
    alt: "Referral Service",
    reverse: true,
  },
  {
    icon: FaWalking,
    title: "Door-Step Therapy",
    text: "Our multi-rehab workers provide door-step services like Physiotherapy, Occupational Therapy, Speech Therapy, and Special Education to 600 CWDs in Puri District, ensuring regular therapy and follow-up during home visits.",
    image: "/assets/images/backgrounds/helth3.png",
    alt: "Therapy Session",
    reverse: false,
  },
];

const HealthDisability = () => {
  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* ================= Hero (rebuilt to 1521 x 482, matching your other pages) ================= */}
      <section className="hd-page">
        <div className="hd-container">
          <motion.div
            className="hd-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="hd-hero__image"
              style={{
                backgroundImage: `url("${resolveAsset(
                  "assets/images/backgrounds/Health & Disability.jpg"
                )}"), url("${resolveAsset(
                  "assets/images/backgrounds/Health.jpg"
                )}"), url("${resolveAsset(
                  "build/assets/images/backgrounds/Health.jpg"
                )}")`,
              }}
            />
            <div className="hd-hero__overlay" />

            <div className="hd-hero__content">
              <div className="hd-hero__mark">
                <FaRegHeart className="hd-hero__mark-heart" />
                <FaHandHoldingHeart className="hd-hero__mark-hand" />
                <svg className="hd-hero__squiggle" viewBox="0 0 220 120" fill="none">
                  <path
                    d="M2 2 C 2 40, 2 70, 40 90 S 150 118, 215 108"
                    stroke="#f2622e"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>

              <motion.h2
                className="hd-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Health & Disability
              </motion.h2>
              <motion.p
                className="hd-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Vikash provides health support for people with disabilities,
                ensuring access to essential care, treatment, and
                rehabilitation services.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Core Programs ================= */}
      <section className="hd-services">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="hd-services__header"
          >
            <h2 className="hd-heading">Our Health Support Services</h2>
            <span className="hd-heading__underline" />
            <p className="hd-services__lead">
              We provide a comprehensive range of services from assessment to
              therapy.
            </p>
          </motion.div>

          {SERVICES.map((service) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={staggerContainer}
                className="hd-serviceRow"
              >
                <Row className="align-items-center justify-content-center g-5">
                  <motion.div
                    className={`col-lg-6 ${service.reverse ? "order-lg-2" : ""}`}
                    variants={staggerItem}
                  >
                    <img
                      src={service.image}
                      alt={service.alt}
                      className="hd-serviceRow__image"
                    />
                  </motion.div>
                  <motion.div
                    className={`col-lg-6 ${service.reverse ? "order-lg-1" : ""}`}
                    variants={staggerItem}
                  >
                    <div className="hd-serviceCard">
                      <span className="hd-serviceCard__icon">
                        <Icon />
                      </span>
                      <h3 className="hd-serviceCard__title">{service.title}</h3>
                      <span className="hd-serviceCard__underline" />
                      <p className="hd-serviceCard__text">{service.text}</p>
                    </div>
                  </motion.div>
                </Row>
              </motion.div>
            );
          })}
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="hd-cta">
        <Container>
          <motion.div
            className="hd-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <span className="hd-cta__leaf" aria-hidden="true">
              🌿
            </span>

            <span className="hd-cta__icon">
              <FaHandHoldingHeart />
            </span>

            <div className="hd-cta__content">
              <h2 className="hd-cta__title">Support Essential Healthcare</h2>
              <span className="hd-cta__underline" />
              <p className="hd-cta__text">
                Your donation helps us provide vital therapy, assistive
                devices, and medical support to people with disabilities.
              </p>
              <div className="hd-cta__actions">
                <button
                  type="button"
                  className="hd-btn hd-btn--solid"
                  onClick={openDonation}
                >
                  <FaRegHeart /> Donate Now
                </button>
                <button
                  type="button"
                  className="hd-btn hd-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default HealthDisability;